import { cn } from '@/utils/cn';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';

interface LogoProps {
  className?: string;
}

/**
 * Logo component - displays operator logo if available, otherwise Monta logo
 */
export function Logo({ className }: LogoProps) {
  const theme = useOperatorStore((s) => s.theme);
  const operator = useOperatorStore((s) => s.operator);
  const logoBgColor = useUiModeStore((s) => s.logoBgColor);

  // If we have an operator theme with a logo, use that
  if (theme?.logo_url) {
    // Background priority: ?logoBgColor= param → theme primary color → black
    const backgroundColor = logoBgColor || theme.primary_color || '#000000';
    return (
      <div className={cn('flex items-center', className)}>
        <div
          className="p-3 rounded-lg shadow-monta-sm"
          style={{ backgroundColor }}
        >
          <img
            src={theme.logo_url}
            alt={`${operator?.name || 'Operator'} Logo`}
            className="h-5"
            style={{ maxWidth: '100px' }}
          />
        </div>
      </div>
    );
  }

  // Default Monta logo
  return (
    <div className={cn('flex items-center', className)}>
      <svg
        width="120"
        height="28"
        viewBox="0 0 149 28"
        fill="currentColor"
        className="text-ink"
        aria-label="Monta"
      >
        <g clipPath="url(#clip0_1924_2917)">
          <path
            d="M27.0229 27.8718C26.8948 27.8718 26.7642 27.8417 26.6461 27.7839L13.9359 21.4841L1.2258 27.7839C0.962053 27.9145 0.650579 27.8995 0.401902 27.7437C0.153225 27.588 0 27.3167 0 27.0228V0.848971C0 0.55508 0.153225 0.281285 0.401902 0.125548C0.653091 -0.0276774 0.967076 -0.0427487 1.22831 0.0903812L13.9359 6.48062L26.6436 0.0903812C26.9073 -0.0427487 27.2188 -0.0276774 27.47 0.125548C27.7212 0.278773 27.8719 0.552568 27.8719 0.846459V27.0228C27.8719 27.3167 27.7212 27.588 27.47 27.7437C27.3344 27.8291 27.1786 27.8718 27.0229 27.8718ZM15.845 20.5371L26.1739 25.6564V15.4154L15.845 20.5346V20.5371ZM1.69552 15.4179V25.6589L12.0244 20.5396L1.69552 15.4179ZM2.71033 14.0263L13.9359 19.5902L25.1616 14.0263L13.9359 8.37961L2.71033 14.0263ZM15.8249 7.43011L26.1764 12.6373V2.22548L15.8249 7.43262V7.43011ZM1.69552 2.22548V12.6373L12.047 7.43011L1.69552 2.22548Z"
            fill="currentColor"
          />
          <path
            d="M41.8984 0.562683H49.4818L56.8743 22.283L64.3421 0.562683H71.6969V27.4273H67.0098L67.163 4.63947L59.2757 27.4273H54.3222L46.4349 4.63947L46.5505 27.4273H41.9009V0.562683H41.8984ZM74.5278 17.8997C74.5278 11.9541 78.9111 7.8773 84.9296 7.8773C90.948 7.8773 95.3313 11.9541 95.3313 17.8997C95.3313 23.8454 90.9882 27.9975 84.9296 27.9975C78.8709 27.9975 74.5278 23.9207 74.5278 17.8997ZM84.9296 24.3779C88.4362 24.3779 90.6466 21.6726 90.6466 17.8997C90.6466 14.1269 88.4738 11.4969 84.9698 11.4969C81.4657 11.4969 79.215 14.049 79.215 17.8997C79.215 21.7504 81.4632 24.3779 84.9321 24.3779H84.9296ZM98.0466 8.45001H102.505V10.9644C103.191 9.8215 105.173 7.8773 108.526 7.8773C112.603 7.8773 115.652 10.354 115.652 15.2321V27.4248H111.081V15.84C111.081 12.7152 109.556 11.5723 107.155 11.5723C103.917 11.5723 102.658 14.0867 102.658 17.4777V27.4223H98.0466V8.45001ZM120.224 21.4063V11.7657H116.986V8.45001H120.224V3.64979H124.796V8.45001H129.063V11.7657H124.796V21.331C124.796 23.1596 125.672 23.7323 127.272 23.7323H129.063V27.4298H125.901C122.244 27.4298 120.224 25.2947 120.224 21.4088V21.4063ZM130.809 22.2453C130.809 19.54 132.218 17.7867 134.848 16.797C136.755 16.1113 139.191 15.8827 142.469 15.8827H144.185V15.1593C144.185 13.3306 143.346 11.2332 139.842 11.2332C137.099 11.2332 135.765 12.7202 135.65 14.471H131.266C131.533 10.6605 134.924 7.87981 139.917 7.87981C145.594 7.87981 148.644 10.8137 148.644 15.6918V27.4273H144.338V24.9506C143.499 26.1312 141.633 28 137.441 28C133.248 28 130.809 25.7519 130.809 22.2453ZM138.815 24.722C142.243 24.722 144.341 22.6648 144.341 19.5777V18.8166H142.703C140.035 18.8166 138.32 18.9698 137.177 19.427C135.881 19.9595 135.424 20.8361 135.424 22.0569C135.424 23.5037 136.491 24.7245 138.815 24.7245V24.722Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_1924_2917">
            <rect width="148.641" height="28" fill="currentColor" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}
